import { Fragment } from 'react'
import { Listbox, Transition } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'

export interface SelectOption {
  value: string
  label: string
  icon?: React.ReactNode
}

interface SelectProps {
  value: string
  onChange: (value: string) => void
  options: SelectOption[]
  placeholder?: string
  className?: string
}

export default function Select({ value, onChange, options, placeholder, className = '' }: SelectProps) {
  const selectedOption = options.find((opt) => opt.value === value)

  return (
    <Listbox value={value} onChange={onChange}>
      {({ open }) => (
        <div className={`relative ${className}`}>
          <Listbox.Button className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-200 shadow-soft hover:border-primary-300 hover:shadow-soft-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300">
            <span className="flex items-center">
              {selectedOption?.icon && (
                <span className="mr-2 flex-shrink-0 transition-transform duration-200 group-hover:scale-110">{selectedOption.icon}</span>
              )}
              <span className="block truncate text-sm text-gray-900">
                {selectedOption?.label || placeholder || '请选择'}
              </span>
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon
                className={`h-5 w-5 text-gray-400 transition-all duration-300 ${
                  open ? 'rotate-180 text-primary-600' : ''
                }`}
                aria-hidden="true"
              />
            </span>
          </Listbox.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Listbox.Options className="absolute z-10 mt-2 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-sm shadow-soft-xl ring-1 ring-gray-100 focus:outline-none backdrop-blur-sm">
              {options.map((option, index) => (
                <Listbox.Option
                  key={option.value}
                  className={({ active }) =>
                    `relative cursor-pointer select-none py-2.5 pl-3 pr-9 transition-all duration-200 animate-fade-in ${
                      active ? 'bg-gradient-to-r from-primary-50 to-primary-100/50 text-primary-900' : 'text-gray-900'
                    }`
                  }
                  value={option.value}
                  style={{ animationDelay: `${index * 30}ms` }}
                >
                  {({ selected, active }) => (
                    <>
                      <div className="flex items-center">
                        {option.icon && (
                          <span className={`mr-2 flex-shrink-0 transition-transform duration-200 ${active ? 'scale-110' : ''}`}>{option.icon}</span>
                        )}
                        <span className={`block truncate transition-all ${selected ? 'font-semibold' : 'font-normal'}`}>
                          {option.label}
                        </span>
                      </div>

                      {selected && (
                        <span
                          className={`absolute inset-y-0 right-0 flex items-center pr-3 transition-all duration-200 ${
                            active ? 'text-primary-600 scale-110' : 'text-primary-600'
                          }`}
                        >
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      )}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      )}
    </Listbox>
  )
}
